import { AUTH_ROUTE_PATHS, type AuthRoutePath, type AuthSearch } from "@jti/fe-common/routes/auth-routes";

export type AuthHrefResolver = (options: { route: AuthRoutePath; search?: AuthSearch }) => string;

const addQueryParam = (url: URL, searchParamName: string, searchParamValue: string): string => {
  if (!url.hash.startsWith("#/")) {
    url.searchParams.set(searchParamName, searchParamValue);
    return url.href;
  }

  const hashQueryIndex = url.hash.indexOf("?");
  const hashPath = hashQueryIndex === -1 ? url.hash : url.hash.slice(0, hashQueryIndex);
  const hashQuery = hashQueryIndex === -1 ? "" : url.hash.slice(hashQueryIndex + 1);
  const searchParams = new URLSearchParams(hashQuery);
  searchParams.set(searchParamName, searchParamValue);
  url.hash = `${hashPath}?${searchParams.toString()}`;

  return url.href;
};

const finalizeRedirectionUrl = (url: string): string => {
  try {
    const absoluteUrl = new URL(url, window.location.origin);

    return addQueryParam(absoluteUrl, "returnUrl", window.location.href);
  } catch {
    return url;
  }
};

export const getRedirectionConfigurationUrl = (
  url: string,
  redirectToAuthPage: boolean,
  isLogged: boolean | undefined,
  resolveConfiguredAuthHref: AuthHrefResolver,
  redirectAfterLoginUrl: string
): string => {
  if (url) {
    return redirectToAuthPage ? resolveConfiguredAuthHref({ route: AUTH_ROUTE_PATHS.login }) : url;
  }

  if (!isLogged) {
    return resolveConfiguredAuthHref({ route: AUTH_ROUTE_PATHS.login });
  }

  return redirectAfterLoginUrl;
};

export const getRedirectionUrl = (
  url: string,
  redirectToAuthPage: boolean,
  isLogged: boolean | undefined,
  resolveConfiguredAuthHref: AuthHrefResolver,
  redirectAfterLoginUrl: string
): string => {
  const hasConfiguredUrl = url.length > 0;
  const shouldRedirectConfiguredUrlToAuth = hasConfiguredUrl && redirectToAuthPage;
  const shouldRedirectToDefaultAuth = !hasConfiguredUrl && !isLogged;

  if (shouldRedirectConfiguredUrlToAuth) {
    const authHref = resolveConfiguredAuthHref({
      route: AUTH_ROUTE_PATHS.login,
      search: { returnUrl: window.location.href },
    });

    return finalizeRedirectionUrl(authHref);
  }

  if (shouldRedirectToDefaultAuth) {
    const authHref = resolveConfiguredAuthHref({
      route: AUTH_ROUTE_PATHS.login,
      search: { returnUrl: window.location.href },
    });

    return finalizeRedirectionUrl(authHref);
  }

  const configuredUrl = getRedirectionConfigurationUrl(
    url,
    redirectToAuthPage,
    isLogged,
    resolveConfiguredAuthHref,
    redirectAfterLoginUrl
  );

  return finalizeRedirectionUrl(configuredUrl);
};
